import { z } from 'zod';
import { MoneySchema } from './money.js';
/**
 * Provisionen und Provisionsrechnungen. Siehe `docs/02_DOMAIN_MODEL.md` §2.5 und D19.
 *
 * Kein Zahlungsdienstleister im MVP: eigener Nummernkreis, PDF über die Puppeteer-Engine,
 * Versand über Resend, Zahlung per Überweisung, Abgleich von Hand.
 *
 * **Diese Items tragen niemals TTL.** Sie sind rechnungsrelevant und unterliegen der
 * zehnjährigen Aufbewahrung nach § 147 AO. Ein Löschbegehren führt zur Anonymisierung,
 * nicht zum Hard-Delete (§4.4).
 *
 * ### Die eine Regel, die dieses Schema trägt
 *
 * **`overdue` wird nie gespeichert.** Genauso wenig, wie `closed` bei einer Ausschreibung aus
 * dem Statusfeld kommt: Das Zahlungsziel ist ein Zeitstempel (`dueAt`), und der abgeleitete
 * Zustand entsteht beim Lesen über {@link effectiveCommissionStatus} (Goldene Regel 4). Der
 * tägliche Cron verschickt nur Erinnerungen und setzt die Einladungssperre — fällt er aus,
 * zeigt die Admin-Ansicht trotzdem das Richtige.
 */
export const CommissionStatusSchema = z.enum([
    /** Zuschlag erteilt, Provision entstanden, noch nicht fakturiert. */
    'accrued',
    /** Rechnung erzeugt und versendet. */
    'invoiced',
    /** Zahlungseingang gebucht. */
    'paid',
    /**
     * Zahlungsziel überschritten.
     *
     * **Abgeleitet, nie gespeichert** — siehe {@link effectiveCommissionStatus}. Der Wert steht
     * in diesem Enum, weil Oberflächen und Filter ihn brauchen; ein Schreibvorgang, der ihn
     * ablegt, wäre ein Fehler.
     */
    'overdue',
    /** Ausgebucht — uneinbringlich. Anders als `void`: Der Anspruch bestand, er ist nur verloren. */
    'uncollectible',
    /**
     * Storniert. Nicht optional: Veranstaltungen werden abgesagt. Ohne definierten Stornoweg
     * verschickt man Rechnungen für nicht erbrachte Leistungen — der schnellste Weg, einen
     * Lieferanten zu verlieren (D10: Meldung binnen 14 Tagen, Admin-Workflow).
     */
    'void',
]);
/** Die Zustände, die tatsächlich am Item stehen dürfen. `overdue` fehlt hier mit Absicht. */
export const STORED_COMMISSION_STATUSES = [
    'accrued',
    'invoiced',
    'paid',
    'uncollectible',
    'void',
];
/** Zustände, aus denen noch Geld kommen kann — die Arbeitsliste des Betreibers. */
export const OPEN_COMMISSION_STATUSES = ['accrued', 'invoiced', 'overdue'];
export const COMMISSION_STATUS_LABELS = {
    accrued: 'Offen',
    invoiced: 'Rechnung gestellt',
    overdue: 'Überfällig',
    paid: 'Bezahlt',
    uncollectible: 'Ausgebucht',
    void: 'Storniert',
};
/**
 * Der abgeleitete Zustand einer Provision. **Das Zahlungsziel ist ein Zeitstempel, kein Status.**
 *
 * Dieselbe Bauart wie `effectiveRfqStatus`: Der Cron schreibt nichts, was sich rechnen lässt.
 * Er verschickt Erinnerungen und setzt die Sperre — beides sind Seiteneffekte, keine Wahrheit.
 */
export function effectiveCommissionStatus(record, now = new Date()) {
    if (record.status !== 'invoiced' || record.dueAt === null)
        return record.status;
    return now.getTime() >= Date.parse(record.dueAt) ? 'overdue' : 'invoiced';
}
/** Volle Tage seit Fälligkeit. `0`, solange die Rechnung nicht überfällig ist. */
export function daysOverdue(dueAt, now = new Date()) {
    if (dueAt === null)
        return 0;
    const diffMs = now.getTime() - Date.parse(dueAt);
    return diffMs <= 0 ? 0 : Math.floor(diffMs / (24 * 60 * 60 * 1000));
}
/* --------------------------------------------------------------------------------------------
 * Nummernkreis
 * ----------------------------------------------------------------------------------------- */
/**
 * Rechnungsnummer im eigenen Nummernkreis: `CTX-<Jahr>-<lfd. ab 10000>`.
 *
 * Die laufende Nummer kommt aus einem atomaren DynamoDB-Zähler (`ADD seq :one`), nie aus
 * Read-then-write. Jedes Jahr startet automatisch bei 10000 (§0.5 im Umsetzungsplan).
 */
export const InvoiceNumberSchema = z.string().regex(/^CTX-\d{4}-\d{5,}$/, 'Format: CTX-<Jahr>-<Nummer>');
/**
 * Der Versatz, mit dem aus `seq = 1` die Nummer `10000` wird.
 *
 * Warum überhaupt ein Versatz: Der Zähler wird nie vorbefüllt — der erste `ADD seq :one`
 * eines Jahres legt das Item an und liefert `1`. Ohne Versatz hieße die erste Rechnung des
 * Jahres `CTX-2026-00001`, und man sähe jeder Rechnung an, die wievielte sie ist. Mit dem
 * Versatz beginnt jedes Jahr bei 10000, ohne dass irgendwo ein Startwert gepflegt werden muss.
 */
export const INVOICE_SEQ_OFFSET = 9999;
/** `(2026, 1)` → `"CTX-2026-10000"`. */
export function invoiceNumberFor(year, seq) {
    if (!Number.isInteger(year) || year < 2000 || year > 9999) {
        throw new RangeError(`Ungültiges Rechnungsjahr: ${year}`);
    }
    if (!Number.isInteger(seq) || seq < 1) {
        throw new RangeError(`Ungültige laufende Nummer: ${seq}`);
    }
    return `CTX-${year}-${String(INVOICE_SEQ_OFFSET + seq).padStart(5, '0')}`;
}
/* --------------------------------------------------------------------------------------------
 * Mahnwesen
 * ----------------------------------------------------------------------------------------- */
/**
 * Die Mahnstufen. **Zwei, und dann ist Schluss** — was danach kommt, ist keine Software mehr,
 * sondern ein Telefonat oder ein Anwalt.
 *
 * Beide Stufen löst der Betreiber im MVP von Hand aus (Phasenplan). Der tägliche Cron
 * verschickt ausschließlich die **Erinnerung am Fälligkeitstag** und meldet dem Betreiber,
 * was überfällig geworden ist.
 */
export const DunningLevelSchema = z.enum(['reminder', 'first']);
export const DUNNING_LEVEL_LABELS = {
    reminder: 'Zahlungserinnerung',
    first: '1. Mahnung',
};
/** Numerischer Rang, damit „nicht zurückstufen" als Bedingung formulierbar ist. */
export const DUNNING_LEVEL_RANK = { reminder: 1, first: 2 };
/**
 * Karenztage nach Fälligkeit, bevor die Einladungssperre greift.
 *
 * Nicht null: Eine Überweisung ist am Fälligkeitstag oft schon raus und am nächsten Werktag
 * da. Wer einen Betrieb am Tag nach dem Zahlungsziel aus dem Matching nimmt, sperrt regelmäßig
 * Lieferanten, die bezahlt haben — und zwar genau die, die man am Anfang am dringendsten
 * braucht.
 */
export const BILLING_BLOCK_GRACE_DAYS = 5;
/* --------------------------------------------------------------------------------------------
 * Der Datensatz
 * ----------------------------------------------------------------------------------------- */
export const CommissionRecordSchema = z.object({
    commissionId: z.string().min(1),
    status: CommissionStatusSchema,
    supplierId: z.string().min(1),
    rfqId: z.string().min(1),
    /**
     * Bemessungsgrundlage: die Angebotssumme netto **bei Zuschlag** (D10). Nachträgliche
     * Erhöhungen der Auftragssumme bleiben provisionsfrei — der Wert hier friert das ein.
     */
    offerNetCents: z.int().min(0),
    /** Der bei Zuschlag geltende, aus der Einladung eingefrorene Satz (D16). */
    commissionRateBp: z.int().min(0).max(10000),
    /** Die Provision selbst — immer 19 % USt (B2B-Vermittlung im Inland). */
    amount: MoneySchema,
    /**
     * Firmenname des Lieferanten, kopiert bei Zuschlag.
     *
     * Eine Rechnungsanschrift, die dem aktuellen Stammdatensatz folgt, ändert rückwirkend
     * ausgestellte Rechnungen — genau das darf sie nicht. Deshalb friert sie hier ein.
     */
    supplierCompanyName: z.string().max(200).default(''),
    /**
     * Datum der Vermittlungsleistung (§ 14 Abs. 4 Nr. 6 UStG). Das ist der **Zuschlagstag**,
     * nicht der Veranstaltungstag: Vermittelt ist, wenn der Vertrag zustande kommt.
     */
    serviceDate: z.iso.date(),
    /** Für die Leistungsbeschreibung auf der Rechnung — Termin und Ort der Veranstaltung. */
    eventDate: z.iso.date(),
    eventCity: z.string().max(120).default(''),
    invoiceNumber: InvoiceNumberSchema.nullable().default(null),
    invoicedAt: z.iso.datetime().nullable().default(null),
    invoicePdfS3Key: z.string().nullable().default(null),
    /** Zahlungsziel: 10 Tage netto ab Rechnungsdatum. Trägt den Zeitindex für das Mahnwesen. */
    dueAt: z.iso.datetime().nullable().default(null),
    paidAt: z.iso.datetime().nullable().default(null),
    /** Verwendungszweck aus dem Kontoauszug — der Beleg des manuellen Abgleichs. */
    paymentReference: z.string().max(200).nullable().default(null),
    voidedAt: z.iso.datetime().nullable().default(null),
    voidReason: z.string().max(500).nullable().default(null),
    /** Zuletzt versendete Mahnstufe. `null` heißt: noch keine. */
    dunningLevel: DunningLevelSchema.nullable().default(null),
    lastDunnedAt: z.iso.datetime().nullable().default(null),
    /** Wann der Cron die Erinnerung am Fälligkeitstag verschickt hat — genau einmal. */
    dueReminderSentAt: z.iso.datetime().nullable().default(null),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/* --------------------------------------------------------------------------------------------
 * Sichten
 * ----------------------------------------------------------------------------------------- */
/**
 * Was der **Lieferant** über seine eigene Provision sieht.
 *
 * Einschlussliste, kein `.omit()` (§7): Ein Feld, das später am Item ergänzt wird, ist hier
 * per Default unsichtbar. Was hier fehlt und fehlen muss: interne Stornogründe, Mahnstand und
 * der S3-Schlüssel des PDF — die Datei gibt es ausschließlich über eine vorsignierte URL.
 */
export const CommissionSupplierViewSchema = z.object({
    commissionId: z.string().min(1),
    /** **Abgeleitet**, nicht das gespeicherte Feld. */
    status: CommissionStatusSchema,
    rfqId: z.string().min(1),
    eventDate: z.iso.date(),
    eventCity: z.string(),
    offerNetCents: z.int().min(0),
    commissionRateBp: z.int().min(0).max(10000),
    amount: MoneySchema,
    invoiceNumber: InvoiceNumberSchema.nullable(),
    invoicedAt: z.iso.datetime().nullable(),
    dueAt: z.iso.datetime().nullable(),
    paidAt: z.iso.datetime().nullable(),
    /** Ob ein PDF bereitsteht. Der Schlüssel selbst bleibt drinnen. */
    hasInvoicePdf: z.boolean(),
    createdAt: z.iso.datetime(),
});
/** Eine Zeile der Provisionsliste im Admin-Bereich. */
export const CommissionAdminListItemSchema = CommissionSupplierViewSchema.extend({
    supplierId: z.string().min(1),
    supplierCompanyName: z.string(),
    paymentReference: z.string().nullable(),
    voidReason: z.string().nullable(),
    dunningLevel: DunningLevelSchema.nullable(),
    lastDunnedAt: z.iso.datetime().nullable(),
    /** Wie viele volle Tage die Rechnung überfällig ist. `0`, solange sie es nicht ist. */
    daysOverdue: z.int().min(0),
});
/* --------------------------------------------------------------------------------------------
 * Eingaben
 * ----------------------------------------------------------------------------------------- */
/**
 * Die Eingriffe des Betreibers an einer Provision.
 *
 * Discriminated Union statt vier Endpunkten — dieselbe Bauart wie `RfqAdminActionSchema`: eine
 * Route, eine Zugriffsprüfung, ein Handler, in dem alle Übergänge nebeneinanderstehen und
 * sichtbar ist, dass jeder von ihnen bedingt geschrieben wird.
 */
export const CommissionAdminActionSchema = z.discriminatedUnion('action', [
    z.object({
        action: z.literal('record_payment'),
        /** Wertstellung aus dem Kontoauszug — **nicht** „heute". */
        paidAt: z.iso.date(),
        /** Verwendungszweck. Der Beleg des Abgleichs; ohne ihn ist die Buchung nicht prüfbar. */
        paymentReference: z.string().min(1).max(200),
    }),
    z.object({
        action: z.literal('void'),
        /** **Pflicht.** Ein Storno ohne Grund ist in einer Betriebsprüfung nicht erklärbar. */
        reason: z.string().min(3).max(500),
    }),
    z.object({
        action: z.literal('write_off'),
        reason: z.string().min(3).max(500),
    }),
    z.object({
        action: z.literal('dunning'),
        level: DunningLevelSchema,
    }),
]);
/* --------------------------------------------------------------------------------------------
 * Rechnungsstammdaten und Rechnungsbeleg
 * ----------------------------------------------------------------------------------------- */
/** Rechnungsstammdaten des Ausstellers — Quelle ist SSM `/caterinx/<env>/billing/*`, nie der Code. */
export const BillingIssuerSchema = z.object({
    companyName: z.string().min(1),
    addressLines: z.array(z.string().min(1)).min(2),
    vatId: z.string().min(1),
    iban: z.string().min(15).max(34),
    bic: z.string().min(8).max(11),
    /** Zahlungsziel in Tagen. */
    paymentTermDays: z.int().min(1).max(90),
    invoiceEmail: z.email(),
});
/**
 * Alles, was auf der Rechnung stehen muss — und nichts, was nicht darauf gehört.
 *
 * Die Pflichtangaben nach § 14 Abs. 4 UStG sind hier **Felder**, keine Vorlagenentscheidung:
 *
 * | § 14 Abs. 4 UStG                  | Feld                                            |
 * | --------------------------------- | ----------------------------------------------- |
 * | Nr. 1 Anschriften beider Seiten   | `issuer.addressLines`, `recipient.addressLines` |
 * | Nr. 2 Steuernummer / USt-IdNr.    | `issuer.vatId`                                  |
 * | Nr. 3 Ausstellungsdatum           | `invoiceDate`                                   |
 * | Nr. 4 fortlaufende Nummer         | `invoiceNumber`                                 |
 * | Nr. 5 Art und Umfang der Leistung | `description`                                   |
 * | Nr. 6 Leistungszeitpunkt          | `serviceDate`                                   |
 * | Nr. 7 Entgelt, nach Satz getrennt | `netCents`, `vatRateBp`                         |
 * | Nr. 8 Steuersatz und Steuerbetrag | `vatRateBp`, `vatCents`                         |
 *
 * Wer die Vorlage ändert, sieht am fehlenden Feld, was er wegwirft. Das ist der ganze Grund,
 * warum dieses Schema existiert und die Vorlage nicht einfach den Datensatz bekommt.
 */
export const InvoiceDocumentSchema = z.object({
    invoiceNumber: InvoiceNumberSchema,
    /** Ausstellungsdatum, ISO-Datum. */
    invoiceDate: z.iso.date(),
    /** Leistungszeitpunkt der Vermittlung — der Zuschlagstag. */
    serviceDate: z.iso.date(),
    dueDate: z.iso.date(),
    issuer: BillingIssuerSchema,
    recipient: z.object({
        companyName: z.string().min(1),
        addressLines: z.array(z.string()).min(1),
        vatId: z.string().nullable(),
    }),
    /** Art und Umfang der Leistung, ein Satz. */
    description: z.string().min(1).max(500),
    /** Bemessungsgrundlage, damit der Lieferant den Satz nachrechnen kann. */
    offerNetCents: z.int().min(0),
    commissionRateBp: z.int().min(0).max(10000),
    netCents: z.int().min(0),
    vatRateBp: z.int().min(0).max(10000),
    vatCents: z.int().min(0),
    grossCents: z.int().min(0),
    /** Verwendungszweck der Überweisung. Immer die Rechnungsnummer — sonst nichts. */
    paymentReference: z.string().min(1),
});
